import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";
import DesignUploaderHelper from "../helpers/design_uploader_helper";

// The redemption page. Shares showNewUpload/hideNewUpload with give_checkout_controller.js
// through DesignUploaderHelper. Its select-image modal is a Turbo frame, as are the design tray
// and both checkout swap targets -- nothing in the give flow binds jquery-pjax any more.
//
// Not covered by checkout_redemption_cluster_spec.rb: hero image drag-to-resize and reposition,
// slick-carousel viewport toggling, and the `redemptionPageUpdated` re-init. closeEditBannerModal
// has no call site anywhere in the app and is dead.
const MOBILE_BREAKPOINT = 768;
const MIN_HERO_HEIGHT = 300;
const DEFAULT_HERO_HEIGHT = 700;
const SCALE_DOWN_FACTOR = 0.384096024006; // Factor used to scale down the hero image in the visualizer

const parsePropsInt = (prop, heroImage) =>
  parseInt(getComputedStyle(heroImage).getPropertyValue(prop), 10);

const heroImageProperties = (heroImage) => ({
  heroImageBgPositionY: parsePropsInt("background-position-y", heroImage),
  heroImageWidth: parsePropsInt("width", heroImage),
  heroImageHeight: parsePropsInt("height", heroImage)
});

const getEventType = (element) =>
  element.classList.contains("btn-hero-image-resizer") ? "resize" : "reposition";

export default class extends Controller {
  connect() {
    this.mobileOnlyMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    this.imageCalculationCache = new Map();

    this.redemptionVisualizer = document.querySelector(".redemption-visualizer");
    this.mobileVisualizer = document.querySelector(".mobile-visualizer");
    this.desktopVisualizer = document.querySelector(".desktop-visualizer");
    this.desktopHeroImageVisualization = this.desktopVisualizer?.querySelector(".hero-image-container");
    this.mobileHeroImageVisualization = this.mobileVisualizer?.querySelector(".hero-image-container");

    this.onRedemptionPageUpdated = this.onRedemptionPageUpdated.bind(this);
    this.onMobileOnlyMediaQueryChange = this.onMobileOnlyMediaQueryChange.bind(this);
    this.onDesignFrameLoad = this.onDesignFrameLoad.bind(this);

    // The select-image modal's container is a Turbo frame now. turbo:frame-load is the
    // pjax:end equivalent. It fires on the frame, but the frame element can be replaced, so
    // delegate off this.element and filter by id -- a reference captured here goes stale on
    // replacement and the listener silently stops firing (P8).
    this.element.addEventListener("turbo:frame-load", this.onDesignFrameLoad);

    document.addEventListener("redemptionPageUpdated", this.onRedemptionPageUpdated);
    this.mobileOnlyMediaQuery.addEventListener("change", this.onMobileOnlyMediaQueryChange);

    DesignUploaderHelper.initialize();
    this.initializeImageEvents().then(() => {
      if (this.mobileOnlyMediaQuery.matches) { this.initializeSlick(); }
    });
  }

  disconnect() {
    document.removeEventListener("redemptionPageUpdated", this.onRedemptionPageUpdated);
    this.mobileOnlyMediaQuery.removeEventListener("change", this.onMobileOnlyMediaQueryChange);

    this.element.removeEventListener("turbo:frame-load", this.onDesignFrameLoad);
  }

  onDesignFrameLoad(event) {
    if (event.target.id !== "give-frame") { return; }

    $.smoothScroll({ scrollTarget: "#give-frame", offset: -200 });
  }

  // --- composed-in behaviour, now shared with give_checkout_controller.js via the same helper ---

  showNewUpload() {
    DesignUploaderHelper.showNewUpload($(window).width() < MOBILE_BREAKPOINT);
  }

  hideNewUpload() {
    DesignUploaderHelper.hideNewUpload();
  }

  // --- this page's own behaviour ---

  nextStep(e) {
    e.preventDefault();
    $(this.element).find("form#redemption-form").submit();
  }

  // A thin wrapper kept from when this controller was also composed into checkout:
  // toggleEditRedemptionImageModal is called with no args everywhere else, and `forceClosing` is
  // only ever set from here.
  toggleGivesRedemptionEditImageModal(event) {
    const forceClosing = event?.params?.forceClosing || false;
    this.toggleEditRedemptionImageModal(forceClosing);

    if (forceClosing) {
      this.destroySlick();
      document.querySelector(".redemption-visualizer").classList.remove("edit-image-modal-open");
    } else {
      this.showCarouselAlongsideImageEditModal();
      document.querySelector(".redemption-visualizer").classList.toggle("edit-image-modal-open");
    }
  }

  toggleEditRedemptionImageModal(forceClosing = false) {
    if (forceClosing) {
      document.querySelector(".edit-redemption-image-modal").classList.remove("show");
      document.querySelector("body").classList.remove("no-scroll");
      document.querySelector(".btn-edit-image").classList.remove("hidden");
    } else {
      document.querySelector(".edit-redemption-image-modal").classList.toggle("show");
      document.querySelector("body").classList.remove("no-scroll");
      document.querySelector(".btn-edit-image").classList.toggle("hidden");
    }
  }

  openEditBannerModal() {
    // Dispatched for give_redemption_mobile_modal_controller.js to consume -- lazily loads the
    // hero image into its modal on this custom event.
    document.dispatchEvent(new CustomEvent("editBannerModalOpen"));
  }

  closeEditBannerModal() {
    $("#edit-banner-modal").modal("hide"); // Bootstrap modal
  }

  toggleInstructionsModal() {
    $("#instructions-modal").modal("show"); // Bootstrap modal
  }

  // --- hero image visualizer: drag-to-resize/reposition, aspect ratio, slick carousel ---
  // Not covered by the spec: pixel-drag and viewport interactions are too flaky under headless
  // Capybara to assert on.

  onRedemptionPageUpdated() {
    this.redemptionVisualizer = document.querySelector(".redemption-visualizer");
    this.mobileVisualizer = document.querySelector(".mobile-visualizer");
    this.desktopVisualizer = document.querySelector(".desktop-visualizer");
    this.desktopHeroImageVisualization = this.desktopVisualizer?.querySelector(".hero-image-container");
    this.mobileHeroImageVisualization = this.mobileVisualizer?.querySelector(".hero-image-container");

    this.initializeImageEvents().then(() => {
      if (this.mobileOnlyMediaQuery.matches) { this.initializeSlick(); }
    });
  }

  onMobileOnlyMediaQueryChange(e) {
    if (e.matches) {
      this.initializeSlick();
    } else {
      this.showCarouselAlongsideImageEditModal();
    }
  }

  useDefaultHeroHeight() {
    return this.redemptionVisualizer.dataset.useDefaultHeight === "true";
  }

  getFormAction() {
    return document.querySelector("#redemption-form").getAttribute("action");
  }

  updateRedemptionImageSettings(heroImage, isMobile) {
    const path = this.getFormAction();
    const { heroImageBgPositionY, heroImageWidth, heroImageHeight } = heroImageProperties(heroImage);
    let params;

    if (isMobile) {
      const mobileOffsetRatio = heroImageBgPositionY / heroImageHeight;

      heroImage.dataset.mobileOffsetRatio = mobileOffsetRatio;
      params = { build: { mobile_offset_ratio: mobileOffsetRatio } };
    } else {
      const aspectRatio = Math.abs(heroImageHeight / heroImageWidth);
      const offsetRatio = heroImageBgPositionY / heroImageHeight;

      heroImage.dataset.aspectRatio = aspectRatio;
      heroImage.dataset.offsetRatio = offsetRatio;
      params = { build: { aspect_ratio: aspectRatio, offset_ratio: offsetRatio } };
    }

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetch(path, options).then((response) => {
      if (!response.ok) { console.warn(response); }
    }).catch((error) => console.warn(error));
  }

  setInitialCacheValues(e, imageProperties, originY, isMobile) {
    const eventType = getEventType(e.target.closest(".postal-interactive-btn") || e.target);

    this.imageCalculationCache.set("imageProperties", imageProperties);
    this.imageCalculationCache.set("eventType", eventType);
    this.imageCalculationCache.set("originY", originY);
    this.imageCalculationCache.set("mouseDown", true);
    this.imageCalculationCache.set("isMobile", isMobile);
  }

  interactionStart(e, heroImageVisualization, isMobile) {
    // We don't allow the banner to be adjusted on mobile from the visualizer
    // to do it we use the edit banner modal
    if (this.mobileOnlyMediaQuery.matches) { return; }

    const clientY = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;

    this.setInitialCacheValues(e, heroImageProperties(heroImageVisualization), clientY, isMobile);

    if (this.imageCalculationCache.get("eventType") === "resize") {
      heroImageVisualization.classList.add("resizing");
    }
  }

  interactionMove(e, heroImageVisualization) {
    if (!this.imageCalculationCache.get("mouseDown")) { return; }

    e.preventDefault();

    const eventType = this.imageCalculationCache.get("eventType");
    const originY = this.imageCalculationCache.get("originY");
    const properties = this.imageCalculationCache.get("imageProperties");
    const isMobile = this.imageCalculationCache.get("isMobile");
    const scalingFactor = isMobile ? 1 : SCALE_DOWN_FACTOR;
    const clientY = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;

    if (eventType === "resize") {
      let newHeight = properties.heroImageHeight + ((clientY - originY) * (1 / scalingFactor));

      if (newHeight <= MIN_HERO_HEIGHT) { newHeight = MIN_HERO_HEIGHT; }

      heroImageVisualization.style.maxHeight = `${newHeight}px`;
    } else if (eventType === "reposition") {
      let newBgPositionY = properties.heroImageBgPositionY + ((clientY - originY) * (1 / scalingFactor));

      if (newBgPositionY > 0) { newBgPositionY = 0; }

      heroImageVisualization.style.backgroundPositionY = `${newBgPositionY}px`;
    }
  }

  interactionEnd(heroImageVisualization) {
    if (!this.imageCalculationCache.get("mouseDown")) { return; }
    if (this.imageCalculationCache.get("eventType") === "resize") {
      heroImageVisualization.classList.remove("resizing");
    }
    this.updateRedemptionImageSettings(heroImageVisualization, this.imageCalculationCache.get("isMobile"));
    this.imageCalculationCache.clear();
  }

  addHeroImageEvents(visualizer, heroImageVisualization, isMobile) {
    heroImageVisualization.addEventListener("mousedown", (e) => this.interactionStart(e, heroImageVisualization, isMobile));
    heroImageVisualization.addEventListener("touchstart", (e) => this.interactionStart(e, heroImageVisualization, isMobile));
    visualizer.addEventListener("mousemove", (e) => this.interactionMove(e, heroImageVisualization));
    visualizer.addEventListener("touchmove", (e) => this.interactionMove(e, heroImageVisualization));
    visualizer.addEventListener("mouseup", () => this.interactionEnd(heroImageVisualization));
    visualizer.addEventListener("mouseleave", () => this.interactionEnd(heroImageVisualization));
    visualizer.addEventListener("touchend", () => this.interactionEnd(heroImageVisualization));
  }

  calculateDefaultHeroImagePositionY(heroImageNaturalHeight, heroImageRatio, heroImageContainerWidth) {
    const scaledImageWidth = DEFAULT_HERO_HEIGHT * heroImageRatio;

    if (!this.useDefaultHeroHeight()) { return 0; }
    if (heroImageNaturalHeight <= DEFAULT_HERO_HEIGHT) { return 0; }
    if (scaledImageWidth >= heroImageContainerWidth) { return 0; }

    return (DEFAULT_HERO_HEIGHT - (heroImageContainerWidth / heroImageRatio)) / 2;
  }

  resize(defaultHeroPositionY, heroImageVisualization, heroImageRatio, visualizationWrapper) {
    const width = heroImageVisualization.offsetWidth;
    const aspectRatio = parseFloat(this.redemptionVisualizer.dataset.aspectRatio);

    let offsetRatio = parseFloat(this.redemptionVisualizer.dataset.offsetRatio);
    let mobileOffsetRatio = parseFloat(this.redemptionVisualizer.dataset.mobileOffsetRatio);

    offsetRatio = Number.isNaN(offsetRatio) ? false : offsetRatio;
    mobileOffsetRatio = Number.isNaN(mobileOffsetRatio) ? 0 : mobileOffsetRatio;

    const useAspectRatio = visualizationWrapper.classList.contains("desktop-visualizer") ? aspectRatio : 1 / heroImageRatio;
    const useOffsetRatio = visualizationWrapper.classList.contains("desktop-visualizer") ? offsetRatio : mobileOffsetRatio;

    const maxHeight = (this.useDefaultHeroHeight() ? DEFAULT_HERO_HEIGHT : parseInt(width * useAspectRatio));
    const backgroundPositionY = parseInt(maxHeight * useOffsetRatio);

    if (offsetRatio !== false) {
      heroImageVisualization.style.backgroundPositionY = `${backgroundPositionY}px`;
    } else {
      heroImageVisualization.style.backgroundPositionY = `${defaultHeroPositionY}px`;
    }

    heroImageVisualization.style.maxHeight = `${maxHeight}px`;
  }

  setHeroImageAspectRatio(visualizationWrapper, heroImageVisualization) {
    return new Promise((resolve) => {
      const img = new Image();
      const bgImage = getComputedStyle(heroImageVisualization).getPropertyValue("background-image");
      img.src = bgImage.replace(/"/g, "").replace(/url\(|\)$/ig, "");

      img.onload = () => {
        const heroImageNaturalWidth = img.naturalWidth;
        const heroImageNaturalHeight = img.naturalHeight;
        const heroImageRatio = heroImageNaturalWidth / heroImageNaturalHeight;

        const defaultHeroPositionY = this.calculateDefaultHeroImagePositionY(
          heroImageNaturalHeight, heroImageRatio, heroImageVisualization.offsetWidth
        );

        heroImageVisualization.style.aspectRatio = heroImageRatio;

        if (this.redemptionVisualizer.dataset.aspectRatio == null) {
          this.redemptionVisualizer.dataset.aspectRatio = heroImageRatio;
        }

        this.resize(defaultHeroPositionY, heroImageVisualization, heroImageRatio, visualizationWrapper);

        resolve();
      };
    });
  }

  initializeImageEvents() {
    if (this.mobileHeroImageVisualization) {
      const categoriesHolder = document.querySelector(".categories-holder");

      if (categoriesHolder) { categoriesHolder.classList.add("no-affix"); }

      // NOTE: Mobile hero image adjustments are disabled for now
      // addHeroImageEvents(mobileVisualizer, mobileHeroImageVisualization, true);
      this.addHeroImageEvents(this.desktopVisualizer, this.desktopHeroImageVisualization, false);

      return Promise.all([
        this.setHeroImageAspectRatio(this.mobileVisualizer, this.mobileHeroImageVisualization),
        this.setHeroImageAspectRatio(this.desktopVisualizer, this.desktopHeroImageVisualization)
      ]);
    }

    return Promise.resolve();
  }

  initializeSlick() {
    const $container = $(document.querySelector(".redemption-visualizer"));
    const sliderNav = document.querySelector(".lasso-2-slick-slider-nav");

    if (!$container.length || $container.children().length === 0) { return; }

    if (sliderNav) { sliderNav.classList.remove("hidden"); }

    $container.slick({
      draggable: false,
      arrows: true,
      dots: true,
      nextArrow: $("#next-arrow"),
      prevArrow: $("#prev-arrow"),
      appendDots: $(".slider-dots")
    });
  }

  destroySlick() {
    const $container = $(document.querySelector(".redemption-visualizer"));
    const sliderNav = document.querySelector(".lasso-2-slick-slider-nav");

    if ($container.hasClass("slick-initialized")) { $container.slick("unslick"); }
    if (sliderNav) { sliderNav.classList.add("hidden"); }
  }

  showCarouselAlongsideImageEditModal() {
    if (window.matchMedia("(min-width: 768px)").matches && window.matchMedia("(max-width: 991px)").matches) {
      if (document.querySelector(".edit-redemption-image-modal").classList.contains("show")) {
        this.initializeSlick();
      } else {
        this.destroySlick();
      }
    } else if (window.matchMedia("(min-width: 992px)").matches) {
      this.destroySlick();
    }
  }
}
